/**
 * Spectrum BXAY bundle cart engine (simple, load-gated) + GoKwik checkout.
 *
 * Adapter over the storefront bundle-cart SDK (`window.__spectrumAi.bundle`,
 * with a `window.Spectrum.bundle` fallback). The server owns the cart and the
 * discount; every mutation returns the enriched bundle cart, and this engine
 * maps it into the state the bottom bar paints. No client pricing, no discount
 * minting — the on-page total is the amount charged at checkout.
 *
 * Load model: there is deliberately NO optimistic cache. init() waits for the
 * SDK, fetches the authoritative cart, and only then flips `ready` to true. The
 * runtime keeps every control disabled until `ready`, so the shopper never acts
 * on unresolved state — which sidesteps the whole optimistic-vs-server race
 * surface (out-of-order responses, stale carts, etc.).
 *
 * Checkout hand-off: the bundle lives in its own server-side cart, which no
 * checkout provider can read. checkout() therefore MOVES it into the native
 * Shopify cart — clear, add, apply the server-minted discount — and hands over
 * to the side cart, which reads the native cart and carries its own checkout
 * button. The move is permanent by design: the shopper continues from the cart
 * we just wrote, so nothing is snapshotted and nothing is restored.
 *
 * Clearing first is what keeps the fixed bundle price honest — the discount is
 * a cart-level code minted for exactly these lines, and an unrelated item left
 * in the cart would be priced against a code never scoped to it.
 *
 * Public surface (consumed by the runtime in the bundle-builder runtime JS):
 *   init(config)            – bind to the bundle slug + hydrate from the server
 *   add(line)               – async; append one unit of a variant, returns ok
 *   remove(index)           – async; drop the unit at a bottom-bar index
 *   clear()                 – async; empty the bundle cart
 *   getState()              – sync snapshot { ..., ready }
 *   countForProduct(handle) – units of a product currently in the cart
 *   canAdd(line)            – sync display gate for the Mini-PDP CTA
 *   subscribe(cb)           – returns an unsubscribe fn; cb fires on every change
 *   checkout()              – async; moves the bundle into the native cart and
 *                             opens the side cart to check out from
 */
;(() => {
  const win = globalThis.window
  const NS = win.Spectrum ?? {}
  if (NS.bxayCart) return // already mounted
  win.Spectrum = NS

  /** POST to a Shopify Ajax cart route with the one header/credentials shape every
   *  cart write here uses. Returns the Response (callers check `.ok`). */
  function cartPost(path, body) {
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: body == null ? undefined : JSON.stringify(body),
    })
  }

  const state = {
    bundleSlug: null,
    config: null,
    lastData: null,
    // Variant that must occupy one slot of every cart for this bundle. Null for
    // free-choice bundles. Enforced client-side only: the backend prices
    // whatever lines it is given, so this constrains the UI, not the offer.
    fixedVariantId: null,
    // false until the first authoritative getCart resolves — the runtime keeps
    // controls disabled while false, so nothing is actionable on unloaded state.
    ready: false,
    subscribers: new Set(),
  }

  function bundleApi() {
    return win.__spectrumAi?.bundle ?? win.Spectrum?.bundle ?? null
  }

  /** True when `variantId` is the bundle's locked variant. String-compares
   *  because the config carries a Liquid-rendered string while cart lines
   *  carry numbers. */
  function isFixedVariant(variantId) {
    return (
      state.fixedVariantId != null && String(variantId) === String(state.fixedVariantId)
    )
  }

  /** Put the locked variant back in the bundle cart when it is missing. Runs
   *  after init and after clear — the two points where the cart can legitimately
   *  come back without it. A rejected add is logged, never faked: the bar then
   *  shows the honest short count rather than a slot the server does not have. */
  async function ensureFixedVariant() {
    if (!state.fixedVariantId) return
    const bundle = bundleApi()
    if (!bundle || !state.bundleSlug) return
    if (getState().groups.some((g) => isFixedVariant(g.variantId))) return
    await commit(
      'ensureFixed',
      bundle.addCart(state.bundleSlug, [
        { id: Number(state.fixedVariantId), quantity: 1 },
      ]),
    )
  }

  /** Resolve the bundle-cart SDK, tolerating its async load order. The edge cart
   *  bundle attaches `__spectrumAi.bundle` from a CDN script that can run AFTER
   *  the theme upgrades this element, so a one-shot read at init races it. Poll
   *  briefly; resolve the SDK once present, or null if it never loads. */
  function waitForBundleApi(timeoutMs = 5000, intervalMs = 100) {
    const immediate = bundleApi()
    if (immediate) return Promise.resolve(immediate)
    return new Promise((resolve) => {
      const deadline = Date.now() + timeoutMs
      const timer = setInterval(() => {
        const bundle = bundleApi()
        if (bundle || Date.now() >= deadline) {
          clearInterval(timer)
          resolve(bundle)
        }
      }, intervalMs)
    })
  }


  /** Map a backend BundleCartResponse into the runtime's bottom-bar state.
   *  Pure — no side effects, no SDK calls — so it can be unit-tested directly. */
  function deriveState(data, config) {
    const progress = data?.spectrumBundleProgress ?? null
    const cost = data?.spectrumBundleCost ?? null
    const items = Array.isArray(data?.items) ? data.items : []

    const lines = items.flatMap((it) => {
      const entry = {
        productHandle: it.handle,
        variantId: it.variant_id,
        image: it.image,
        displayLabel: it.product_title,
        price: it.final_price,
        compareAtPrice: it.original_price,
      }
      const qty = Math.max(1, Number(it.quantity) || 1)
      return Array.from({ length: qty }, () => entry)
    })

    const groups = items.map((it) => ({
      variantId: it.variant_id,
      productHandle: it.handle,
      displayLabel: it.product_title,
      variantTitle: it.variant_title ?? '',
      options: Array.isArray(it.options_with_values) ? it.options_with_values : [],
      image: it.image,
      price: it.final_price,
      compareAtPrice: it.original_price,
      quantity: Math.max(1, Number(it.quantity) || 1),
    }))

    const count = progress?.eligibleItemCount ?? lines.length
    const required = progress?.requiredItemCount ?? config?.quantityRequirement ?? 0
    const isMet = !!progress?.isSatisfied
    const total = cost?.totalAmount ?? 0
    const retailTotal = cost?.totalMRP ?? 0
    const savings = cost?.applicableDiscount ?? 0
    const partialOk = !!config?.partialCheckoutAllowed

    return {
      lines,
      groups,
      count,
      tier: { quantity: required, price: total, label: null, isMet, isExact: isMet, nextTier: null },
      total,
      retailTotal,
      savings,
      canCheckout: isMet || (partialOk && count > 0),
    }
  }

  function getState() {
    return { ...deriveState(state.lastData, state.config), ready: state.ready }
  }

  function notify() {
    const snapshot = getState()
    for (const cb of state.subscribers) {
      try {
        cb(snapshot)
      } catch (err) {
        console.warn('[bxay-cart] subscriber failed', err)
      }
    }
  }

  /** Await an SDK call, store its cart on success, and notify. Failures surface
   *  loudly — a rejected add (out of stock, cap) must not look like success. */
  async function commit(action, promise) {
    let result
    try {
      result = await promise
    } catch (err) {
      console.warn(`[bxay-cart] ${action} threw`, err)
      return { ok: false, error: String(err) }
    }
    if (result?.ok && result.data) {
      state.lastData = result.data
      notify()
    } else {
      console.warn(`[bxay-cart] ${action} failed`, result?.error ?? result)
    }
    return result
  }

  const api = {
    async init(config) {
      state.config = config || {}
      state.bundleSlug = config?.bundleSlug ?? null
      state.fixedVariantId = config?.fixedVariantId || null
      if (!state.bundleSlug) {
        console.warn('[bxay-cart] inactive — missing config.bundleSlug')
        state.ready = true
        notify()
        return
      }
      const bundle = await waitForBundleApi()
      if (!bundle) {
        console.warn('[bxay-cart] inactive — bundle SDK did not load', {
          bundleSlug: state.bundleSlug,
        })
        state.ready = true
        notify()
        return
      }
      // `ready` stays false across BOTH the getCart and the locked-variant seed.
      // Every notify() in that window paints the disabled state, so the grid
      // never enables on a cart that is still missing its mandatory slot — a
      // click landing there would spend the shopper's only free pick on a second
      // product and leave the bundle unsatisfiable.
      await commit('init', bundle.getCart(state.bundleSlug))
      await ensureFixedVariant()
      state.ready = true
      // Unconditional: the seed and the initial fetch both notify only when they
      // stored a cart, so a failed or empty init would otherwise leave the
      // controls disabled forever with no way back.
      notify()
    },

    async add(line, qty = 1) {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return false
      const quantity = Math.max(1, Math.floor(Number(qty) || 1))
      const result = await commit(
        'add',
        bundle.addCart(state.bundleSlug, [{ id: Number(line.variantId), quantity }]),
      )
      return !!result?.ok
    },

    async remove(index) {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return false
      const lines = getState().lines
      const line = lines[index]
      if (!line) return false
      const variantId = line.variantId
      if (isFixedVariant(variantId)) return false
      const remaining = lines.filter((l, i) => l.variantId === variantId && i !== index).length
      const result = await commit(
        'remove',
        bundle.changeCart(state.bundleSlug, { id: Number(variantId), quantity: remaining }),
      )
      return !!result?.ok
    },

    async clear() {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return
      await commit('clear', bundle.clearCart(state.bundleSlug))
      await ensureFixedVariant()
    },

    getState,

    isFixedVariant,

    countForProduct(handle) {
      return getState().lines.filter((l) => l.productHandle === handle).length
    },

    quantityForVariant(variantId) {
      return (
        getState().groups.find((g) => String(g.variantId) === String(variantId))?.quantity ?? 0
      )
    },

    groupsForProduct(handle) {
      return getState().groups.filter((g) => g.productHandle === handle)
    },

    async setQuantity(variantId, qty) {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return false
      // The locked variant holds exactly one slot: a stepper must not be able to
      // zero it out, nor stack a second copy where the shopper's free pick goes.
      if (isFixedVariant(variantId)) return false
      const next = Math.max(0, Math.floor(Number(qty) || 0))
      const result = await commit(
        'setQuantity',
        bundle.changeCart(state.bundleSlug, { id: Number(variantId), quantity: next }),
      )
      return !!result?.ok
    },

    /** Synchronous display gate for the Mini-PDP CTA. */
    canAdd(line) {
      const s = getState()
      const cap = state.config?.quantityRequirement || Number.POSITIVE_INFINITY
      if (s.count >= cap) return false
      if (
        state.config?.quantityRestriction === 'max_one_per_product' &&
        line?.productHandle &&
        s.lines.some((l) => l.productHandle === line.productHandle)
      ) {
        return false
      }
      return true
    },

    subscribe(cb) {
      state.subscribers.add(cb)
      try {
        cb(getState())
      } catch {}
      return () => state.subscribers.delete(cb)
    },

    /** Route the bundle through the NATIVE Shopify theme cart, then open the GoKwik
     *  checkout modal — GoKwik checks out the native cart, not the bundle's separate
     *  Storefront-API cart. Snapshots the shopper's cart (in memory + into GoKwik's
     *  own localStorage restore keys), mirrors the bundle into the theme cart,
     *  applies the discount, opens GoKwik in-page, and restores the shopper's cart on
     *  close. The clear is gated on a captured snapshot and every write is checked, so
     *  a failure never loses the cart or opens checkout at the wrong price.
     *  Returns { ok, reason? }. */
    async checkout() {
      const bundle = bundleApi()
      if (!bundle || !state.bundleSlug) return { ok: false, reason: 'no_bundle' }

      const result = await commit('checkout', bundle.getCart(state.bundleSlug))
      if (!result?.ok || !result.data) return { ok: false, reason: 'refresh_failed' }
      const data = result.data
      if (!deriveState(data, state.config).canCheckout) {
        return { ok: false, reason: 'not_satisfied' }
      }

      // Items the bundle qualifies on → theme-cart add payload.
      const items = (Array.isArray(data.items) ? data.items : [])
        .map((it) => ({ id: it.variant_id, quantity: Math.max(1, Number(it.quantity) || 1) }))
        .filter((it) => it.id != null)
      if (items.length === 0) return { ok: false, reason: 'no_items' }

      // The fixed-bundle-price discount the backend minted + applied to the bundle
      // cart. Applicable-only: an inapplicable code would silently no-op.
      const code = (Array.isArray(data.discount_codes) ? data.discount_codes : [])
        .find((d) => d && d.applicable && d.code)?.code ?? null

      // Move the bundle into the native Shopify cart and hand off. The side cart
      // is the checkout surface from here: it reads the native cart, shows the
      // bundle, and carries its own checkout button.
      //
      // This is a MOVE, not a borrow. Nothing is snapshotted and nothing is
      // restored, because the shopper is meant to keep shopping in the cart we
      // just wrote — a restore would delete the bundle out from under them
      // mid-checkout. That is also why GoKwik's own `gkbuynow` / `cartItems`
      // restore keys are deliberately NOT seeded: seeding them would make
      // GoKwik's `dt()` reinstate the pre-bundle cart on the next page load.
      //
      // Clearing first is what keeps the fixed bundle price honest: the code
      // below is a cart-level discount minted for exactly these lines, and any
      // unrelated item left in the cart would be priced against a code that was
      // never scoped to it.
      try {
        const clearRes = await cartPost('/cart/clear.js')
        if (!clearRes.ok) throw new Error(`clear ${clearRes.status}`)
        const addRes = await cartPost('/cart/add.js', { items })
        if (!addRes.ok) {
          console.error('[bxay-cart] theme-cart add failed', addRes.status)
          return { ok: false, reason: 'theme_cart_add_failed' }
        }
        // Confirm the discount took. A cart holding the bundle at full price is
        // worse than a failed checkout, so this is checked rather than assumed.
        if (code) {
          const discRes = await cartPost('/cart/update.js', { discount: code })
          if (!discRes.ok) {
            console.error('[bxay-cart] discount apply failed', discRes.status)
            return { ok: false, reason: 'discount_apply_failed' }
          }
        }
      } catch (err) {
        console.error('[bxay-cart] theme-cart sync failed', err)
        return { ok: false, reason: 'theme_cart_sync_failed' }
      }

      // Ask the side cart to open. It also opens itself in response to the
      // `/cart/add.js` above, so this is belt-and-braces for the case where that
      // reaction is switched off — calling it twice is harmless, never opening it
      // would strand the shopper on a page with no visible next step.
      if (typeof win.openGokwikSideCart === 'function') {
        try {
          win.openGokwikSideCart()
        } catch (err) {
          console.warn('[bxay-cart] openGokwikSideCart threw', err)
        }
      } else {
        console.warn('[bxay-cart] side cart unavailable — cart holds the bundle, no drawer opened')
      }
      return { ok: true }
    },
  }

  if (typeof __SAI_BXAY_TEST__ !== 'undefined') {
    /* istanbul ignore next */
    api.__test = { deriveState }
  }

  NS.bxayCart = api
})()
